import { useEffect, useRef, useState } from 'react'

import { type apiZodios } from '~api/apiZodios'
import { API_URL } from '~config'
import {
  useGenerateAssistantResponseStreamMutation,
  useGenerateResponseStreamMutation,
} from '~redux/slices/apiSlice'
import {
  type AssistantAttachmentDisplay,
  type CaseDisplayLink,
  type ChatStreamStatusStats,
  type ContactDisplayLink,
  type CreatedSkill,
  type ResponseTypeEnumType,
  type SourceDisplayLink,
} from '~types'

export const preTextStreamResponseSteps = [
  'initialization',
  'retrieve_sources',
  'search_entities',
  'gis_lookup',
  'external_api_call',
  'search_contacts',
  'search_cases',
  'generate_document',
  'create_skill',
  'update_skill',
  'generation',
] as const

export const CONNECTION_LOST_MESSAGE = 'Connection lost. Please try entering your message again.'

export type PreTextStreamResponseStep = (typeof preTextStreamResponseSteps)[number]

export type StreamResponseStep =
  | PreTextStreamResponseStep
  | 'web_search'
  | 'vector_search'
  | 'text_search'
  | 'expand_page_source'
  | 'search_page_sources'
  | 'citation'
  | 'partial_response'
  | 'complete_response'
  | 'all_results'
  | 'error'

export type StreamStatusPhase = 'started' | 'complete'

export interface AllResultsSourceDisplayLink extends SourceDisplayLink {
  snippet_pending?: boolean
}

export interface StreamControlFlowAction {
  type: 'sharepoint_reconnect'
  connection_id?: string
}

export interface StreamControlFlow {
  required_actions?: StreamControlFlowAction[]
}

interface StreamResponse {
  step: StreamResponseStep
  user_id?: string
  ai_interaction_id?: string
  response_id?: string
  response_text?: string
  response_type?: ResponseTypeEnumType
  sources?: SourceDisplayLink[]
  all_results?: AllResultsSourceDisplayLink[]
  cases?: CaseDisplayLink[]
  contacts?: ContactDisplayLink[]
  files?: AssistantAttachmentDisplay[]
  skills?: CreatedSkill[]
  has_integrations?: boolean
  control_flow?: StreamControlFlow
  policy_blocked?: boolean
  title?: string
  status_phase?: StreamStatusPhase
  status_stats?: ChatStreamStatusStats
}

export const useStreamAIResponse = (
  onMessage: (response: StreamResponse, isFirstResponse: boolean) => void,
  onError: (errorMessage: string) => void,
  useAssistantEndpoint = false,
): {
  isGeneratingResponseStream: boolean
  streamAIResponse: (
    request:
      | Parameters<typeof apiZodios.ai_generateResponse>[0]
      | Parameters<typeof apiZodios.ai_generateAssistantResponse>[0],
  ) => Promise<
    | {
        title?: string
        ai_interaction_id?: string
        include_search_results?: boolean
        policy_blocked?: boolean
      }
    | undefined
  >
  abortStream: () => void
  resumeStream: (responseStreamId: string) => void
  resetInteractionId: () => void
  aiInteractionId: string | undefined
} => {
  const [generateResponseStream, regularResult] = useGenerateResponseStreamMutation()
  const [generateAssistantResponseStream, assistantResult] =
    useGenerateAssistantResponseStreamMutation()

  const {
    isLoading: isGeneratingResponseStream,
    isError,
    data,
  } = useAssistantEndpoint ? assistantResult : regularResult
  const [resumedResponseStreamId, setResumedResponseStreamId] = useState<string>()
  const responseStreamId = data?.response_stream_id ?? resumedResponseStreamId
  const eventSourceRef = useRef<EventSource | null>(null)
  const isAbortedRef = useRef<boolean>(false)

  // Always call the latest onMessage/onError, not the closure captured when the effect ran.
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    if (isError) {
      onErrorRef.current('Error generating response. Please try entering your message again.')
    }
  }, [isError])

  useEffect(() => {
    if (!responseStreamId) {
      return
    }

    isAbortedRef.current = false

    // Open connection to response stream
    const eventSource = new EventSource(`${API_URL}/ai/read-response-stream/${responseStreamId}/`)
    eventSourceRef.current = eventSource

    let receivedFirstResponseTextChunk = false
    eventSource.onmessage = (event): void => {
      // Ignore messages if stream has been aborted
      if (isAbortedRef.current) {
        return
      }

      try {
        const response = JSON.parse(event.data) as StreamResponse
        if (response.step === 'error') {
          onErrorRef.current('Error reading response. Please try entering your message again.')
          eventSource.close()
          eventSourceRef.current = null
          return
        }

        onMessageRef.current(response, receivedFirstResponseTextChunk)

        // Set flag to true after the first response with response text is received
        if (response.step === 'partial_response') {
          receivedFirstResponseTextChunk = true
        } else if (response.step === 'complete_response') {
          eventSource.close()
          eventSourceRef.current = null
        }
      } catch {
        onErrorRef.current('Error parsing response. Please try entering your message again.')
        eventSource.close()
        eventSourceRef.current = null
      }
    }

    eventSource.onerror = (): void => {
      if (isAbortedRef.current) return
      onErrorRef.current(CONNECTION_LOST_MESSAGE)
      eventSource.close()
      eventSourceRef.current = null
    }

    // Close connection on unmount
    return (): void => {
      eventSource.close()
      eventSourceRef.current = null
    }
  }, [responseStreamId])

  const abortStream = (): void => {
    isAbortedRef.current = true
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }

  const streamAIResponse = async (
    request:
      | Parameters<typeof apiZodios.ai_generateResponse>[0]
      | Parameters<typeof apiZodios.ai_generateAssistantResponse>[0],
  ): Promise<
    | {
        title?: string
        ai_interaction_id?: string
        include_search_results?: boolean
        policy_blocked?: boolean
      }
    | undefined
  > => {
    if (useAssistantEndpoint) {
      return generateAssistantResponseStream(
        request as Parameters<typeof apiZodios.ai_generateAssistantResponse>[0],
      ).unwrap()
    } else {
      return generateResponseStream(
        request as Parameters<typeof apiZodios.ai_generateResponse>[0],
      ).unwrap()
    }
  }

  const resetInteractionId = (): void => {
    regularResult.reset()
    assistantResult.reset()
  }

  return {
    isGeneratingResponseStream,
    streamAIResponse,
    abortStream,
    resumeStream: setResumedResponseStreamId,
    resetInteractionId,
    aiInteractionId: data?.ai_interaction_id,
  }
}
